import { useMemo } from 'react';

import {
	useStarterDashboardV2BreakpointState,
	useStarterDashboardV2PageData,
	useStarterDashboardV2TabsStateContext,
} from '#src/app/pages/starterDashboardV2/pageContext';
import { useProcessedImageUrl } from '#src/app/shared/hooks/image';

import type { ActionButtonProps } from '../../types';

export type UsePageHeaderReturn = ModelHookResult<
	{
		title: string;
		imageUrl?: string;
		isImageProcessing: boolean;
		actionButtons: Array<ActionButtonProps['actionType']>;
		mobile: boolean;
		orientation: 'horizontal' | 'vertical';
		buttonVariant: 'default' | 'square' | 'inline';
	},
	undefined
>;

export const SECONDARY_ACTION_BUTTONS = ['link', 'edit', 'view'] as const;
export const MOBILE_SECONDARY_ACTION_BUTTONS = ['link', 'view', 'edit'] as const;

const useData = () => {
	const {
		data: {
			petition: { ask, photo },
			viewer: {
				experiments: {
					starterExperienceDashboardSunriseV1: { variation },
				},
			},
		},
	} = useStarterDashboardV2PageData();

	return { ask, photo, variation };
};

export function usePageHeader(): UsePageHeaderReturn {
	const { ask, photo, variation } = useData();
	const { isMobile } = useStarterDashboardV2BreakpointState();

	const image = useMemo(() => {
		if (!photo?.petitionSmall.url) return undefined;
		const { url } = photo.petitionSmall;
		const processing = url.includes('#processing');
		return { url, processing };
	}, [photo]);

	const imageUrl = useProcessedImageUrl(image);
	const isImageProcessing = !!image && !imageUrl;

	const shareButtonType = variation === 'variant2' ? 'shareExperimental' : 'share';

	const {
		data: { selectedTab },
	} = useStarterDashboardV2TabsStateContext();
	const actionButtons: Array<ActionButtonProps['actionType']> = useMemo(() => {
		if (!isMobile) {
			// Desktop: always show all buttons
			return [shareButtonType, ...SECONDARY_ACTION_BUTTONS];
		}
		// Mobile
		if (selectedTab === 'home') {
			return [shareButtonType, ...MOBILE_SECONDARY_ACTION_BUTTONS];
		}
		if (selectedTab === 'share') {
			return [shareButtonType];
		}
		return [];
	}, [isMobile, selectedTab, shareButtonType]);

	const orientation = actionButtons.length > 1 ? 'vertical' : 'horizontal';
	const buttonVariant = isMobile && actionButtons.length > 1 ? 'square' : 'default';

	return {
		data: {
			title: ask,
			imageUrl,
			isImageProcessing,
			actionButtons,
			mobile: isMobile,
			orientation,
			buttonVariant,
		},
	};
}
